"use client";

import Image from "next/image";
import { MapPin, Award, Star } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Title } from "@/components/ui/Headings";

type Testimonial = {
  name: string;
  location: string;
  rating: number;
  text: string;
  trip: string;
  image: string;
};

type StarRatingProps = {
  rating: number;
  sizeClass?: string;
  align?: "start" | "center";
};
const StarRating: React.FC<StarRatingProps> = ({
  rating,
  sizeClass = "w-4 h-4",
  align = "start",
}) => {
  const justify = align === "center" ? "justify-center" : "justify-start";
  return (
    <div className={`flex ${justify} items-center gap-1 leading-none`}>
      {Array.from({ length: 5 }).map((_, idx) => (
        <Star
          key={idx}
          className={`${sizeClass} ${
            idx < rating ? "fill-yellow-400 text-yellow-400" : "text-gray-300"
          }`}
        />
      ))}
    </div>
  );
};

const StatsAndTestimonialsSection: React.FC = () => {
  const { t } = useTranslation();
  const testimonials: Testimonial[] = [
    {
      name: t("home.testimonials.jessica.name"),
      location: t("home.testimonials.jessica.location"),
      rating: 5,
      text: t("home.testimonials.jessica.text"),
      trip: t("home.testimonials.jessica.trip"),
      image:
        "https://images.pexels.com/photos/1374510/pexels-photo-1374510.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&fit=crop",
    },
    {
      name: t("home.testimonials.carlos.name"),
      location: t("home.testimonials.carlos.location"),
      rating: 4,
      text: t("home.testimonials.carlos.text"),
      trip: t("home.testimonials.carlos.trip"),
      image:
        "https://images.pexels.com/photos/1040880/pexels-photo-1040880.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&fit=crop",
    },
    {
      name: t("home.testimonials.anna.name"),
      location: t("home.testimonials.anna.location"),
      rating: 5,
      text: t("home.testimonials.anna.text"),
      trip: t("home.testimonials.anna.trip"),
      image:
        "https://images.pexels.com/photos/774909/pexels-photo-774909.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&fit=crop",
    },
  ];

  return (
    <section className="py-4 sm:py-6 lg:py-6 bg-neutral-cream hidden md:block">
      <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8">
        {/* Grid Testimonials */}
        <Title
          size="md"
          className="text-center mb-4 sm:mb-4 lg:mb-4 leading-tight"
        >
          {t("home.stats.testimonialsHeading")}
        </Title>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
          {testimonials.map((t, idx) => (
            <div
              key={idx}
              className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-5 sm:p-6 group hover:-translate-y-1"
            >
              <div className="flex items-center mb-4">
                <div className="relative w-8 h-8 sm:w-10 sm:h-10 mr-3 sm:mr-4 flex-shrink-0">
                  <Image
                    src={t.image}
                    alt={t.name}
                    fill
                    className="rounded-full object-cover border-4 border-primary-50"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold text-sm sm:text-base text-primary-900 truncate">
                    {t.name}
                  </h4>
                  <p className="text-xs text-gray-600 flex items-center">
                    <MapPin className="w-2.5 h-2.5 sm:w-3 sm:h-3 mr-1 text-primary-600 flex-shrink-0" />
                    <span className="truncate">{t.location}</span>
                  </p>
                </div>
              </div>
              <div className="mb-image.png1">
                <StarRating
                  rating={t.rating}
                  align="start"
                  sizeClass="w-2.5 h-2.5"
                />
              </div>
              <p className="text-xs text-gray-900 mb-2 leading-relaxed">
                {t.text}
              </p>
              <div className="flex items-center text-xs sm:text-sm text-primary-700 font-medium">
                <Award className="w-3 h-3 mr-2 flex-shrink-0" />
                <span className="line-clamp-2">{t.trip}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default StatsAndTestimonialsSection;
